import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Shield, Check, X, KeyRound } from "lucide-react";

export default function TwoFactorSettings() {
  const [status, setStatus] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/auth/2fa/status"); setStatus(data); }
    catch (e) { toast.error(extractError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startEnroll = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/2fa/enroll"); setEnrollment(data); }
    catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/verify", { code });
      toast.success("Two-factor authentication is enabled");
      setEnrollment(null); setCode(""); load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    const c = window.prompt("Enter your current 6-digit code to disable 2FA");
    if (!c) return;
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: c });
      toast.success("Two-factor authentication disabled");
      load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-stone2-50">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <span className="section-eyebrow">Account security</span>
        <h1 className="mt-2 text-3xl font-heading font-bold text-sage-900 flex items-center gap-2">
          <Shield className="w-6 h-6 text-sage-600" /> Two-factor authentication
        </h1>
        <p className="text-stone2-600 mt-2 text-sm">
          Add a second step to sign-in using an authenticator app (Google Authenticator, 1Password, Authy, etc.).
        </p>

        <section className="paper-card p-6 mt-8" data-testid="twofa-card">
          {status?.enabled ? (
            <div>
              <div className="flex items-center gap-2 text-sage-900 font-semibold">
                <Check className="w-4 h-4 text-sage-600" /> 2FA is enabled on your account.
              </div>
              <button type="button" onClick={disable} disabled={busy}
                data-testid="twofa-disable-btn"
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-danger text-danger px-4 py-2 text-sm font-semibold hover:bg-danger/5">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />} Disable 2FA
              </button>
            </div>
          ) : !enrollment ? (
            <div>
              <p className="text-sm text-stone2-600">2FA is currently <span className="font-semibold text-stone2-800">not enabled</span>.</p>
              <button type="button" onClick={startEnroll} disabled={busy} data-testid="twofa-enroll-btn"
                className="btn-primary mt-4">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Set up 2FA
              </button>
            </div>
          ) : (
            <div>
              <p className="text-sm text-stone2-600 mb-3">Scan this QR with your authenticator app, then enter the 6-digit code.</p>
              <div className="mx-auto w-fit p-3 bg-white border border-stone2-200 rounded-lg">
                <img
                  src={`data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(enrollment.qr_svg)))}`}
                  alt="2FA QR code"
                  className="block"
                  data-testid="twofa-qr-img"
                />
              </div>
              <div className="mt-3 text-xs text-stone2-600 text-center">
                Or paste this secret manually: <code className="font-mono">{enrollment.secret}</code>
              </div>
              <div className="mt-4 flex gap-2">
                <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  data-testid="twofa-code-input"
                  placeholder="123456" inputMode="numeric" maxLength={6}
                  className="flex-1 rounded-lg border border-stone2-200 bg-white px-4 py-2 text-sm font-mono text-lg text-center tracking-widest focus:ring-2 focus:ring-sage-500 outline-none" />
                <button type="button" onClick={submit} disabled={busy || code.length !== 6}
                  data-testid="twofa-verify-btn"
                  className="btn-primary text-sm">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Enable
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
